/**
 * Enhanced Dashboard Page
 * リッチでインタラクティブなダッシュボードUI
 * ドラッグ&ドロップ、ダークモード、AI予測対応
 */

import { Stack, Button, Group, ActionIcon, Tooltip, Affix, Transition } from '@mantine/core';
import { IconLayoutGrid, IconRefresh, IconX } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import {
  useKpiData,
  useCheckinCheckoutData,
  useWeeklyComparisonData,
  useTodayScheduleData,
  useAttendanceTrendData,
} from '../api/hooks';
import { EnhancedKpiCards } from '../components/EnhancedKpiCards';
import { CheckInOutTabs } from '../components/CheckInOutTabs';
import { WeeklyComparisonChart } from '../components/WeeklyComparisonChart';
import { TodayScheduleGantt } from '../components/TodayScheduleGantt';
import { AttendanceTrendChart } from '../components/AttendanceTrendChart';
import { AIInsightsCard } from '../components/AIInsightsCard';
import {
  EnhancedKpiCardsSkeleton,
  CheckInOutTabsSkeleton,
  WeeklyComparisonChartSkeleton,
  TodayScheduleGanttSkeleton,
  AttendanceTrendChartSkeleton,
} from '../components/skeletons';
import { useConfetti } from '../hooks/useConfetti';
import { useDashboardLayout } from '../hooks/useDashboardLayout';
import { DashboardGrid } from '../components/DashboardGrid';
import type { KpiWithMetrics } from '../types/enhanced';
import '../styles/grid.css';

export const EnhancedDashboard = () => {
  // 真の並列フェッチ：5つの独立したTanStack Query
  const { data: kpiData, isLoading: kpiLoading } = useKpiData();
  const { data: checkinData, isLoading: checkinLoading } = useCheckinCheckoutData();
  const { data: weeklyData, isLoading: weeklyLoading } = useWeeklyComparisonData();
  const { data: scheduleData, isLoading: scheduleLoading } = useTodayScheduleData();
  const { data: trendData, isLoading: trendLoading } = useAttendanceTrendData();

  // Confetti機能
  const { trigger: triggerConfetti, ConfettiComponent } = useConfetti();
  const previousKpi = useRef<KpiWithMetrics | null>(null);

  // ダッシュボードレイアウト管理
  const { layout, onLayoutChange, isEditMode, setIsEditMode, resetLayout } = useDashboardLayout();

  // 目標達成検知（本日の入室目標）
  useEffect(() => {
    if (!kpiData || !previousKpi.current) {
      previousKpi.current = kpiData;
      return;
    }

    // 本日の入室目標達成判定（例: 100名）
    const DAILY_CHECKIN_GOAL = 100;
    const prevCheckins = previousKpi.current.today_checkins.value;
    const currentCheckins = kpiData.today_checkins.value;

    if (prevCheckins < DAILY_CHECKIN_GOAL && currentCheckins >= DAILY_CHECKIN_GOAL) {
      triggerConfetti(5000);
      // 通知も表示可能（オプション）
      // notifications.show({ title: '🎉 目標達成！', message: '本日の入室目標を達成しました！' });
    }

    previousKpi.current = kpiData;
  }, [kpiData, triggerConfetti]);

  return (
    <>
      {ConfettiComponent}

      {/* フローティング編集ボタン（画面右下固定） */}
      <Affix position={{ bottom: 56, right: 20 }} zIndex={100}>
        <Transition transition="slide-up" mounted={true}>
          {(transitionStyles) => (
            <Group gap="xs" style={transitionStyles}>
              {isEditMode && (
                <Tooltip label="デフォルトに戻す">
                  <ActionIcon
                    size="lg"
                    variant="filled"
                    color="red"
                    onClick={resetLayout}
                    style={{
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <IconRefresh size={20} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Tooltip label={isEditMode ? 'レイアウト確定' : 'レイアウト編集'}>
                <Button
                  size="md"
                  variant="filled"
                  color={isEditMode ? 'green' : 'blue'}
                  leftSection={isEditMode ? <IconX size={18} /> : <IconLayoutGrid size={18} />}
                  onClick={() => setIsEditMode(!isEditMode)}
                  style={{
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  {isEditMode ? '完了' : '編集'}
                </Button>
              </Tooltip>
            </Group>
          )}
        </Transition>
      </Affix>

      <Stack gap="md" p="md">
        {/* 常にDashboardGridを使用してレイアウトを反映 */}
        <DashboardGrid layout={layout} onLayoutChange={onLayoutChange} isEditMode={isEditMode}>
            <div key="kpi">
              {!kpiLoading && kpiData ? (
                <EnhancedKpiCards kpi={kpiData} />
              ) : (
                <EnhancedKpiCardsSkeleton />
              )}
            </div>
            <div key="checkin">
              {!checkinLoading && checkinData ? (
                <CheckInOutTabs data={checkinData} />
              ) : (
                <CheckInOutTabsSkeleton />
              )}
            </div>
            <div key="weekly">
              {!weeklyLoading && weeklyData ? (
                <WeeklyComparisonChart data={weeklyData} />
              ) : (
                <WeeklyComparisonChartSkeleton />
              )}
            </div>
            <div key="trend">
              {!trendLoading && trendData ? (
                <AttendanceTrendChart data={trendData} />
              ) : (
                <AttendanceTrendChartSkeleton />
              )}
            </div>
            <div key="insights">
              <AIInsightsCard />
            </div>
            <div key="schedule">
              {!scheduleLoading && scheduleData ? (
                <TodayScheduleGantt schedule={scheduleData} />
              ) : (
                <TodayScheduleGanttSkeleton />
              )}
            </div>
          </DashboardGrid>
      </Stack>
    </>
  );
};
